import z from 'zod';
import { PaginationResponseSchema } from '../common';

export const CHAT_MESSAGE_BODY_MIN_LENGTH = 1;
export const CHAT_MESSAGE_BODY_MAX_LENGTH = 500;

export const CHAT_MODERATION_REASON_MAX_LENGTH = 200;

/**
 * Upper bound (seconds) for `videoOffsetSec` — 48 hours. Longer than
 * any realistic stream duration; anything above this is a bad-actor
 * or client-clock-skew signal and gets rejected at the wire.
 */
export const CHAT_MESSAGE_VIDEO_OFFSET_MAX_SECONDS = 48 * 60 * 60;

export const ChatMessageUserSchema = z.object({
  id: z.string(),
  displayName: z.string(),
  colorHex: z.string().regex(/^#[0-9A-Fa-f]{6}$/),
});

export const ChatMessageSchema = z.object({
  id: z.string(),
  body: z.string(),
  createdAt: z.iso.datetime(),
  user: ChatMessageUserSchema,
  deletedAt: z.iso.datetime().optional(),
  /**
   * Seconds elapsed since the event's live stream started at the moment
   * the client posted the message. Set by the frontend from the local
   * player clock (`Math.floor(player.currentTime)`); omitted for
   * pre-LIVE or catch-up posts where the player wasn't playing.
   *
   * Consumers render chat during replay by filtering to
   * `videoOffsetSec <= currentPlayheadSec` and sorting by
   * `videoOffsetSec ASC` (falling back to `createdAt` for pre-live
   * messages).
   */
  videoOffsetSec: z.number().int().nonnegative().optional(),
});

export const ChatHistoryResponseSchema = z.object({
  items: z.array(ChatMessageSchema),
  ...PaginationResponseSchema.shape,
});

export const PostChatMessageRequestSchema = z.object({
  body: z.string().trim().min(CHAT_MESSAGE_BODY_MIN_LENGTH).max(CHAT_MESSAGE_BODY_MAX_LENGTH),
  videoOffsetSec: z.number().int().min(0).max(CHAT_MESSAGE_VIDEO_OFFSET_MAX_SECONDS).optional(),
});

export const ChatModerationDeleteRequestSchema = z
  .object({
    reason: z.string().trim().max(CHAT_MODERATION_REASON_MAX_LENGTH).optional(),
  })
  .strict();

export const ChatModerationMuteRequestSchema = z
  .object({
    /** Optional explicit future timestamp. Omit for the ~1y indefinite default. */
    until: z.iso
      .datetime()
      .refine((value) => new Date(value).getTime() > Date.now(), {
        message: 'until must be a future timestamp',
      })
      .optional(),
    reason: z.string().trim().max(CHAT_MODERATION_REASON_MAX_LENGTH).optional(),
  })
  .strict();

/**
 * Public-shape response for `POST /chat/mute`. `mutedUntil` is the source
 * of truth (`null` = not muted). `mutedBy` is intentionally omitted —
 * moderation metadata stays out of the response surface.
 */
export const ChatMuteStateResponseSchema = z.object({
  eventId: z.string(),
  mutedUntil: z.iso.datetime().nullable(),
});

export const ChatCredentialsResponseSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('in-process'),
    wsNamespace: z.string(),
    token: z.string(),
    eventRoomId: z.string(),
  }),
  z.object({
    kind: z.literal('ivs'),
    roomIdentifier: z.string(),
    endpoint: z.string(),
    token: z.string(),
    tokenTtlSeconds: z.number().int().positive(),
  }),
]);

export const InProcessChatCredentialsSchema = z.object({
  kind: z.literal('in-process'),
  wsNamespace: z.string(),
  token: z.string(),
  eventRoomId: z.string(),
});
export const IvsChatCredentialsSchema = z.object({
  kind: z.literal('ivs'),
  roomIdentifier: z.string(),
  endpoint: z.string(),
  token: z.string(),
  tokenTtlSeconds: z.number().int(),
});

/**
 * ACK envelope for the `chat.post` client → server frame. Success
 * carries the persisted message so the FE can dedupe against the
 * fan-out echo; failure surfaces a stable machine-readable `code`
 * plus a human-readable `message`.
 */
export const ChatPostAckSchema = z.discriminatedUnion('ok', [
  z.object({
    ok: z.literal(true),
    message: ChatMessageSchema,
  }),
  z.object({
    ok: z.literal(false),
    code: z.enum([
      'unauthorized',
      'invalid_payload',
      'muted',
      'not_live',
      'forbidden',
      'not_found',
      'rate_limited',
      'internal',
    ]),
    message: z.string(),
    retryAfterSeconds: z.number().int().nonnegative().optional(),
  }),
]);
